import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { appConfigDoc } from '@/config/collections';
import { useAuth } from '@/contexts/AuthContext';
import type { AppConfig } from '@/types';

/**
 * Hook to load app configuration from Firestore
 * Includes project type options and other app settings
 */
export function useAppConfig() {
  return useQuery({
    queryKey: ['appConfig'],
    queryFn: async (): Promise<AppConfig | null> => {
      const configDocSnapshot = await getDoc(appConfigDoc(db));

      if (!configDocSnapshot.exists()) {
        return null;
      }

      return configDocSnapshot.data() as AppConfig;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - config doesn't change often
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });
}

/**
 * Hook to update app configuration
 */
export function useUpdateAppConfig() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<AppConfig>) => {
      const docRef = appConfigDoc(db);
      const existingDoc = await getDoc(docRef);
      const existingData = existingDoc.exists() ? existingDoc.data() : {};

      await setDoc(docRef, {
        ...existingData,
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: user?.id || 'unknown',
      }, { merge: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
    },
  });
}
